/**
 * Per-service case-study registry.
 *
 * Each entry supplies the real axes of that service (document types,
 * purposes, the steps we actually perform). The generator combines them with
 * shared audience / destination / province axes to produce 1,000+ distinct
 * situational examples per service.
 */
import type { ServiceCaseConfig } from "./generator";
import { buildServiceTopics } from "./generator";
import type { CaseTopic } from "./types";

const COMMON_EXTRAS = [
  "แปลเอกสารรับรองคำแปล",
  "รับรองลายมือชื่อโดยทนายโนตารี",
  "รับรองนิติกรณ์กรมการกงสุล",
  "รับรองสถานทูต",
  "บริการล่าม",
  "จัดส่งเอกสารระหว่างประเทศ",
];

export const SERVICE_CASES: ServiceCaseConfig[] = [
  {
    slug: "notary",
    th: "รับรองเอกสารโดยทนายโนตารี (Notary Public)",
    hubHref: "/notary",
    summaryTh: "งานรับรองลายมือชื่อ สำเนาถูกต้อง คำสาบาน และหนังสือมอบอำนาจโดยทนายผู้ทำคำรับรองลายมือชื่อ",
    documents: [
      { slug: "poa", th: "หนังสือมอบอำนาจ" },
      { slug: "affidavit", th: "คำสาบาน / Affidavit" },
      { slug: "copy-cert", th: "สำเนาถูกต้องของหนังสือเดินทางและบัตรประชาชน" },
      { slug: "transcript", th: "ทรานสคริปต์และใบปริญญา" },
      { slug: "contract", th: "สัญญาและบันทึกข้อตกลง" },
      { slug: "sponsor", th: "หนังสือรับรองผู้สนับสนุนทางการเงิน" },
      { slug: "company-doc", th: "เอกสารบริษัทและมติที่ประชุม" },
      { slug: "consent", th: "หนังสือยินยอมให้บุตรเดินทาง" },
    ],
    purposesTh: [
      "ใช้ยื่นสมัครเรียนและขอวีซ่านักเรียน",
      "ใช้มอบอำนาจจัดการทรัพย์สินแทน",
      "ใช้ประกอบการเปิดบัญชีและธุรกรรมการเงิน",
      "ใช้ยื่นต่อศาลและหน่วยงานทะเบียน",
      "ใช้ประกอบการจดทะเบียนบริษัทและลงนามสัญญา",
      "ใช้ยืนยันตัวตนต่อหน่วยงานราชการ",
    ],
    stepsTh: [
      "ตรวจรูปแบบเอกสารและถ้อยคำรับรองที่ปลายทางกำหนดก่อนนัดลงนาม",
      "จัดเตรียมถ้อยคำรับรอง (jurat / acknowledgement) ให้ตรงกับวัตถุประสงค์",
      "ตรวจบัตรประจำตัวและความสมัครใจของผู้ลงนามในวันลงนาม",
      "จัดล่ามร่วมเมื่อผู้ลงนามไม่เข้าใจภาษาของเอกสาร",
      "บันทึกเลขทะเบียนคำรับรองและออกชุดสำเนาให้ลูกค้าเก็บอ้างอิง",
      "วางแผนขั้นตอนรับรองต่อเนื่องหลังงานโนตารีตามที่ปลายทางต้องการ",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (สภาทนายความในพระบรมราชูปถัมภ์)",
    citationTh: "อ้างอิงข้อบังคับสภาทนายความว่าด้วยการขึ้นทะเบียนทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร",
  },
  {
    slug: "naati",
    th: "แปลรับรอง NAATI",
    hubHref: "/naati",
    summaryTh: "งานแปลโดยนักแปล NAATI สำหรับใช้กับหน่วยงานออสเตรเลียและนิวซีแลนด์",
    documents: [
      { slug: "id-card", th: "บัตรประชาชนและทะเบียนบ้าน" },
      { slug: "birth", th: "สูติบัตร" },
      { slug: "marriage", th: "ทะเบียนสมรสและใบสำคัญการสมรส" },
      { slug: "divorce", th: "ทะเบียนหย่าและบันทึกข้อตกลง" },
      { slug: "degree", th: "ใบปริญญาและทรานสคริปต์" },
      { slug: "police", th: "หนังสือรับรองความประพฤติ" },
      { slug: "bank", th: "หนังสือรับรองยอดเงินฝาก" },
      { slug: "medical", th: "ใบรับรองแพทย์และประวัติการรักษา" },
    ],
    purposesTh: [
      "ใช้ยื่นวีซ่าคู่ครองและวีซ่าครอบครัว",
      "ใช้ยื่นวีซ่านักเรียนและสมัครสถาบันการศึกษา",
      "ใช้ยื่นประเมินวุฒิและขึ้นทะเบียนวิชาชีพ",
      "ใช้ยื่นขอสัญชาติและถิ่นที่อยู่ถาวร",
      "ใช้ยื่นต่อหน่วยงานสวัสดิการและประกันสังคมปลายทาง",
      "ใช้ประกอบการสมัครงานและตรวจสอบประวัติ",
    ],
    stepsTh: [
      "ตรวจว่าเอกสารต้นทางครบและอ่านได้ทุกหน้าก่อนเข้าคิวแปล",
      "แปลโดยนักแปลที่ได้รับการรับรองและประทับตรารับรองครบตามรูปแบบ",
      "ตรวจทานการสะกดชื่อให้ตรงกับหนังสือเดินทางทุกฉบับ",
      "ส่งไฟล์ตรวจร่างให้ลูกค้ายืนยันก่อนออกฉบับจริง",
      "จัดส่งฉบับจริงและไฟล์สำเนาให้ครบตามจำนวนชุดที่ปลายทางกำหนด",
      "เก็บชุดอ้างอิงไว้ให้ลูกค้าขอสำเนาเพิ่มภายหลังได้",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "หน่วยงานผู้รับเอกสารในออสเตรเลียหรือนิวซีแลนด์",
    citationTh: "อ้างอิงข้อกำหนดการยอมรับคำแปลของหน่วยงานปลายทางและมาตรฐานนักแปลที่ได้รับการรับรอง",
  },
  {
    slug: "sworn",
    th: "แปลโดยนักแปลสาบานตน (Sworn Translation)",
    hubHref: "/sworn",
    summaryTh: "งานแปลที่ต้องใช้นักแปลสาบานตนหรือรูปแบบคำแปลเฉพาะของประเทศปลายทาง",
    documents: [
      { slug: "civil", th: "เอกสารทะเบียนราษฎร" },
      { slug: "court", th: "คำพิพากษาและเอกสารคดี" },
      { slug: "notarial", th: "เอกสารที่ผ่านการรับรองโดยโนตารี" },
      { slug: "corporate", th: "หนังสือรับรองบริษัทและงบการเงิน" },
      { slug: "education", th: "เอกสารการศึกษา" },
      { slug: "estate", th: "พินัยกรรมและเอกสารมรดก" },
      { slug: "insurance", th: "กรมธรรม์และเอกสารเคลม" },
      { slug: "id", th: "เอกสารแสดงตน" },
    ],
    purposesTh: [
      "ใช้ยื่นต่อศาลและหน่วยงานทะเบียนต่างประเทศ",
      "ใช้ประกอบการรับรองเอกสารที่สถานทูต",
      "ใช้ยื่นขอถิ่นที่อยู่และสัญชาติ",
      "ใช้ประกอบธุรกรรมทรัพย์สินข้ามประเทศ",
      "ใช้ยื่นต่อหน่วยงานประกันและกองทุนบำนาญ",
      "ใช้ประกอบการจดทะเบียนสมรสและครอบครัว",
    ],
    stepsTh: [
      "ยืนยันกับปลายทางว่ารับคำแปลรูปแบบใดก่อนเริ่มงาน",
      "จับคู่เอกสารกับนักแปลที่ปลายทางยอมรับรูปแบบคำรับรอง",
      "จัดทำคำรับรองของนักแปลแนบท้ายพร้อมข้อมูลติดต่อที่ตรวจสอบได้",
      "ตรวจสอบเลขที่เอกสารและตราประทับให้ตรงกับต้นฉบับทุกจุด",
      "ประสานขั้นรับรองต่อเนื่องหลังงานแปลตามลำดับของปลายทาง",
      "จัดชุดเอกสารเรียงตามลำดับที่ปลายทางกำหนดเพื่อลดการถูกตีกลับ",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "สถานทูตหรือหน่วยงานทะเบียนของประเทศปลายทาง",
    citationTh: "อ้างอิงประกาศข้อกำหนดคำแปลของสถานทูตหรือหน่วยงานปลายทางแต่ละประเทศ",
  },
  {
    slug: "mlt",
    th: "แปลเอกสารหลายภาษา",
    hubHref: "/translation",
    summaryTh: "งานแปลเอกสารราชการและเอกสารธุรกิจในหลายคู่ภาษา พร้อมรับรองคำแปล",
    documents: [
      { slug: "civil-doc", th: "เอกสารทะเบียนราษฎร" },
      { slug: "biz", th: "เอกสารธุรกิจและสัญญา" },
      { slug: "edu", th: "เอกสารการศึกษา" },
      { slug: "med", th: "เอกสารทางการแพทย์" },
      { slug: "tech", th: "คู่มือและเอกสารเทคนิค" },
      { slug: "fin", th: "เอกสารการเงินและภาษี" },
      { slug: "web", th: "เนื้อหาเว็บไซต์และสื่อประชาสัมพันธ์" },
      { slug: "legal", th: "เอกสารกฎหมายและคำฟ้อง" },
    ],
    purposesTh: [
      "ใช้ยื่นหน่วยงานราชการไทยและต่างประเทศ",
      "ใช้ประกอบการเจรจาและลงนามสัญญาข้ามประเทศ",
      "ใช้ยื่นสมัครเรียนและเทียบวุฒิ",
      "ใช้ประกอบการรักษาพยาบาลและเคลมประกัน",
      "ใช้ประกอบการยื่นภาษีและตรวจสอบบัญชี",
      "ใช้เผยแพร่ข้อมูลองค์กรหลายภาษา",
    ],
    stepsTh: [
      "กำหนดคู่ภาษาและรูปแบบคำรับรองที่ปลายทางต้องการ",
      "จัดทำคลังคำศัพท์เฉพาะงานเพื่อให้คำแปลสอดคล้องกันทุกฉบับ",
      "แปลและตรวจทานสองชั้นโดยผู้ตรวจคนละคนกับผู้แปล",
      "จัดหน้าให้ตรงกับต้นฉบับเพื่อให้เจ้าหน้าที่เทียบได้ง่าย",
      "ออกหนังสือรับรองคำแปลพร้อมข้อมูลผู้แปลที่ติดต่อกลับได้",
      "ส่งไฟล์และฉบับพิมพ์ตามจำนวนชุดที่ต้องใช้จริง",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "หน่วยงานผู้รับเอกสารทั้งในไทยและต่างประเทศ",
    citationTh: "อ้างอิงข้อกำหนดการรับรองคำแปลของกรมการกงสุลและของหน่วยงานปลายทาง",
  },
  {
    slug: "apostille",
    th: "Apostille และการรับรองเอกสารระหว่างประเทศ",
    hubHref: "/apostille",
    summaryTh: "งานเตรียมเอกสารเพื่อขอ Apostille หรือรับรองแบบต่อเนื่องตามข้อกำหนดของประเทศปลายทาง",
    documents: [
      { slug: "birth-cert", th: "สูติบัตรและทะเบียนบ้าน" },
      { slug: "marriage-cert", th: "ทะเบียนสมรส" },
      { slug: "single-status", th: "หนังสือรับรองความเป็นโสด" },
      { slug: "degree-cert", th: "ใบปริญญาและใบรับรองผลการศึกษา" },
      { slug: "police-cert", th: "หนังสือรับรองความประพฤติ" },
      { slug: "company-cert", th: "หนังสือรับรองนิติบุคคล" },
      { slug: "poa-doc", th: "หนังสือมอบอำนาจ" },
      { slug: "court-doc", th: "คำพิพากษาและคำสั่งศาล" },
    ],
    purposesTh: [
      "ใช้จดทะเบียนสมรสหรือบันทึกฐานะครอบครัวในต่างประเทศ",
      "ใช้ยื่นสมัครเรียนและเทียบวุฒิในต่างประเทศ",
      "ใช้ยื่นขอถิ่นที่อยู่และสัญชาติ",
      "ใช้ประกอบการจัดตั้งบริษัทและธุรกรรมข้ามประเทศ",
      "ใช้ประกอบคดีและการจัดการมรดกในต่างประเทศ",
      "ใช้ยื่นต่อหน่วยงานแรงงานและวิชาชีพในต่างประเทศ",
    ],
    stepsTh: [
      "ตรวจว่าประเทศปลายทางรับ Apostille หรือยังต้องรับรองต่อเนื่องที่สถานทูต",
      "จัดลำดับขั้นรับรองให้ถูกลำดับตั้งแต่ต้นทางถึงปลายทาง",
      "จัดทำคำแปลตามรูปแบบที่หน่วยงานรับรองยอมรับ",
      "ยื่นและติดตามคิวรับรองพร้อมแจ้งช่วงเวลาที่คาดหมายเป็นระยะ",
      "ตรวจความถูกต้องของตราประทับและข้อมูลบนใบรับรองก่อนส่งมอบ",
      "แนะนำการเก็บรักษาและอายุการใช้งานของชุดเอกสาร",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "กรมการกงสุล กระทรวงการต่างประเทศ และหน่วยงานปลายทาง",
    citationTh: "อ้างอิงข้อมูลอนุสัญญากรุงเฮกว่าด้วยการยกเลิกการรับรองเอกสารมหาชนต่างประเทศและประกาศของกรมการกงสุล",
  },
  {
    slug: "lgz",
    th: "รับรองนิติกรณ์และรับรองสถานทูต (Legalization)",
    hubHref: "/lgz",
    summaryTh: "งานรับรองเอกสารที่กรมการกงสุลและการรับรองต่อที่สถานทูตของประเทศปลายทาง",
    documents: [
      { slug: "civil-reg", th: "เอกสารทะเบียนราษฎร" },
      { slug: "education-doc", th: "เอกสารการศึกษา" },
      { slug: "commercial", th: "เอกสารการค้าและใบกำกับสินค้า" },
      { slug: "company", th: "เอกสารนิติบุคคล" },
      { slug: "medical-doc", th: "ใบรับรองแพทย์" },
      { slug: "poa-legal", th: "หนังสือมอบอำนาจ" },
      { slug: "police-doc", th: "หนังสือรับรองความประพฤติ" },
      { slug: "religious", th: "หนังสือรับรองสถานะและเอกสารทางศาสนา" },
    ],
    purposesTh: [
      "ใช้ยื่นขอวีซ่าและใบอนุญาตทำงานในต่างประเทศ",
      "ใช้จดทะเบียนสมรสในต่างประเทศ",
      "ใช้ประกอบการส่งออกและงานพาณิชย์",
      "ใช้ยื่นต่อหน่วยงานราชการปลายทาง",
      "ใช้ประกอบการเปิดสาขาและตั้งบริษัทในต่างประเทศ",
      "ใช้ยื่นขอทุนและตำแหน่งงานในต่างประเทศ",
    ],
    stepsTh: [
      "ตรวจข้อกำหนดของสถานทูตแต่ละประเทศซึ่งไม่เหมือนกัน",
      "จัดทำคำแปลและรับรองคำแปลก่อนเข้าขั้นนิติกรณ์",
      "ยื่นรับรองที่กรมการกงสุลและติดตามคิว",
      "นัดหมายและยื่นต่อสถานทูตปลายทางตามรูปแบบที่กำหนด",
      "ตรวจชุดเอกสารก่อนส่งมอบว่าครบทุกตราประทับตามลำดับ",
      "สรุปวันหมดอายุของเอกสารแต่ละฉบับให้ลูกค้าวางแผนยื่น",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "กรมการกงสุล กระทรวงการต่างประเทศ และสถานทูตของประเทศปลายทาง",
    citationTh: "อ้างอิงประกาศบริการรับรองนิติกรณ์เอกสารของกรมการกงสุลและประกาศของสถานทูตแต่ละแห่ง",
  },
  {
    slug: "visa",
    th: "แปลและเตรียมเอกสารสำหรับยื่นวีซ่า",
    hubHref: "/visa",
    summaryTh: "งานจัดชุดเอกสารและคำแปลสำหรับการยื่นวีซ่าแต่ละประเภทและแต่ละประเทศ",
    documents: [
      { slug: "bank-doc", th: "เอกสารทางการเงินและสเตทเมนต์" },
      { slug: "work-cert", th: "หนังสือรับรองการทำงานและสลิปเงินเดือน" },
      { slug: "family-doc", th: "เอกสารความสัมพันธ์ในครอบครัว" },
      { slug: "travel", th: "แผนการเดินทางและหลักฐานที่พัก" },
      { slug: "study", th: "หนังสือตอบรับจากสถาบันการศึกษา" },
      { slug: "property", th: "เอกสารทรัพย์สินและโฉนด" },
      { slug: "sponsor-doc", th: "หนังสือรับรองผู้สนับสนุน" },
      { slug: "insurance-doc", th: "กรมธรรม์ประกันการเดินทางและสุขภาพ" },
    ],
    purposesTh: [
      "ใช้ยื่นวีซ่าท่องเที่ยวและเยี่ยมญาติ",
      "ใช้ยื่นวีซ่านักเรียนและฝึกอบรม",
      "ใช้ยื่นวีซ่าทำงานและย้ายถิ่นฐาน",
      "ใช้ยื่นวีซ่าคู่ครองและครอบครัว",
      "ใช้ยื่นวีซ่าธุรกิจและประชุม",
      "ใช้ยื่นวีซ่าพำนักระยะยาวและเกษียณ",
    ],
    stepsTh: [
      "ตรวจเช็กลิสต์เอกสารของสถานทูตแต่ละแห่งซึ่งปรับปรุงได้ตลอด",
      "แปลและรับรองคำแปลเฉพาะฉบับที่ปลายทางกำหนดให้แปล",
      "จัดเรียงชุดเอกสารตามลำดับในเช็กลิสต์เพื่อลดการถูกขอเพิ่ม",
      "ตรวจความสอดคล้องของตัวเลขและวันที่ระหว่างเอกสารทุกฉบับ",
      "เตรียมเอกสารสำรองสำหรับกรณีเจ้าหน้าที่ขอข้อมูลเพิ่ม",
      "สรุปประเด็นที่มักถูกสอบถามเพื่อให้ลูกค้าเตรียมคำตอบล่วงหน้า",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "สถานทูตหรือศูนย์รับคำร้องขอวีซ่าของประเทศปลายทาง",
    citationTh: "อ้างอิงเช็กลิสต์เอกสารที่ประกาศโดยสถานทูตหรือศูนย์รับคำร้องขอวีซ่าของแต่ละประเทศ",
  },
  {
    slug: "immigration",
    th: "งานตรวจคนเข้าเมืองในประเทศไทย",
    hubHref: "/immigration",
    summaryTh: "งานเอกสารสำหรับการต่อวีซ่า รายงานตัว 90 วัน และเรื่องอื่นที่ยื่นต่อสำนักงานตรวจคนเข้าเมือง",
    documents: [
      { slug: "tm7", th: "คำขออนุญาตอยู่ต่อและเอกสารประกอบ" },
      { slug: "tm30", th: "แจ้งที่พักอาศัยของคนต่างด้าว" },
      { slug: "tm47", th: "การรายงานตัว 90 วัน" },
      { slug: "reentry", th: "คำขอ Re-entry permit" },
      { slug: "residence-cert", th: "หนังสือรับรองถิ่นที่อยู่" },
      { slug: "financial", th: "หลักฐานการเงินสำหรับการอยู่ต่อ" },
      { slug: "marriage-proof", th: "หลักฐานการสมรสและครอบครัว" },
      { slug: "employer", th: "เอกสารนายจ้างและใบอนุญาตทำงาน" },
    ],
    purposesTh: [
      "ใช้ยื่นขออยู่ต่อในราชอาณาจักร",
      "ใช้ประกอบการรายงานตัวตามกำหนด",
      "ใช้ขอหนังสือรับรองเพื่อทำใบขับขี่หรือธุรกรรมอื่น",
      "ใช้ประกอบการเดินทางออกและกลับเข้าประเทศ",
      "ใช้ยื่นเปลี่ยนประเภทการตรวจลงตรา",
      "ใช้ประกอบการจ้างงานคนต่างด้าว",
    ],
    stepsTh: [
      "ตรวจปฏิทินวันครบกำหนดของลูกค้าและวางแผนยื่นล่วงหน้า",
      "จัดเตรียมและแปลเอกสารประกอบตามรูปแบบที่สำนักงานกำหนด",
      "ตรวจความครบถ้วนของลายมือชื่อและสำเนาทุกหน้า",
      "ประสานนายจ้างหรือเจ้าบ้านเพื่อขอเอกสารที่ต้องใช้ร่วม",
      "ติดตามผลและแจ้งกำหนดครั้งถัดไปให้ลูกค้าทราบ",
      "จัดทำชุดสำเนาอ้างอิงสำหรับการยื่นรอบถัดไป",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "สำนักงานตรวจคนเข้าเมือง",
    citationTh: "อ้างอิงประกาศและระเบียบว่าด้วยการอนุญาตให้อยู่ต่อในราชอาณาจักรของสำนักงานตรวจคนเข้าเมือง",
  },
  {
    slug: "longstay",
    th: "วีซ่าพำนักระยะยาวและผู้เกษียณ",
    hubHref: "/longstay",
    summaryTh: "งานเอกสารสำหรับผู้ที่ต้องการพำนักระยะยาวในไทยหรือย้ายไปพำนักในต่างประเทศ",
    documents: [
      { slug: "pension-proof", th: "หลักฐานรายได้และบำนาญ" },
      { slug: "bank-longstay", th: "หนังสือรับรองยอดเงินฝาก" },
      { slug: "health", th: "ประกันสุขภาพและใบรับรองแพทย์" },
      { slug: "criminal", th: "หนังสือรับรองความประพฤติ" },
      { slug: "lease", th: "สัญญาเช่าและหลักฐานที่พัก" },
      { slug: "family-longstay", th: "เอกสารผู้ติดตามและครอบครัว" },
      { slug: "id-longstay", th: "หนังสือเดินทางและเอกสารแสดงตน" },
      { slug: "investment", th: "หลักฐานการลงทุนและทรัพย์สิน" },
    ],
    purposesTh: [
      "ใช้ยื่นขอพำนักระยะยาวเพื่อการเกษียณ",
      "ใช้ยื่นขอวีซ่าประเภทผู้พำนักระยะยาว",
      "ใช้ยื่นขอผู้ติดตามของผู้พำนัก",
      "ใช้ยื่นต่ออายุการพำนักประจำปี",
      "ใช้ประกอบการย้ายภูมิลำเนาไปต่างประเทศ",
      "ใช้ประกอบการรับสิทธิด้านสุขภาพและประกัน",
    ],
    stepsTh: [
      "ตรวจเงื่อนไขรายได้และประกันตามที่ประกาศกำหนด",
      "จัดทำคำแปลเอกสารรายได้และบำนาญให้ตรงรูปแบบผู้รับ",
      "ประสานธนาคารเพื่อขอหนังสือรับรองในรูปแบบที่ยอมรับ",
      "จัดลำดับการรับรองเอกสารต่างประเทศให้ทันวันยื่น",
      "จัดทำปฏิทินการต่ออายุและรายงานตัวรายปี",
      "เตรียมชุดเอกสารสำรองสำหรับผู้ติดตาม",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "สำนักงานตรวจคนเข้าเมืองหรือหน่วยงานตรวจคนเข้าเมืองของประเทศปลายทาง",
    citationTh: "อ้างอิงประกาศหลักเกณฑ์การพำนักระยะยาวของหน่วยงานตรวจคนเข้าเมืองที่เกี่ยวข้อง",
  },
  {
    slug: "workpermit",
    th: "ใบอนุญาตทำงานและเอกสารนายจ้าง",
    hubHref: "/work-permit",
    summaryTh: "งานเอกสารสำหรับการขอและต่อใบอนุญาตทำงานของคนต่างด้าวในไทย",
    documents: [
      { slug: "wp-form", th: "คำขอใบอนุญาตทำงานและเอกสารประกอบ" },
      { slug: "employer-doc", th: "เอกสารนิติบุคคลของนายจ้าง" },
      { slug: "degree-wp", th: "วุฒิการศึกษาและหนังสือรับรองประสบการณ์" },
      { slug: "medical-wp", th: "ใบรับรองแพทย์" },
      { slug: "contract-wp", th: "สัญญาจ้างและรายละเอียดตำแหน่ง" },
      { slug: "tax-wp", th: "เอกสารภาษีและประกันสังคม" },
      { slug: "visa-wp", th: "การตรวจลงตราประเภทที่เกี่ยวข้อง" },
      { slug: "renewal", th: "เอกสารต่ออายุและเปลี่ยนนายจ้าง" },
    ],
    purposesTh: [
      "ใช้ขอใบอนุญาตทำงานครั้งแรก",
      "ใช้ต่ออายุใบอนุญาตทำงาน",
      "ใช้เปลี่ยนนายจ้างหรือเปลี่ยนตำแหน่งงาน",
      "ใช้ประกอบการยื่นวีซ่าประเภททำงาน",
      "ใช้ประกอบการตรวจสอบของหน่วยงานแรงงาน",
      "ใช้ประกอบการจ้างงานผู้เชี่ยวชาญต่างชาติ",
    ],
    stepsTh: [
      "ตรวจคุณสมบัติตำแหน่งงานกับเงื่อนไขที่กฎหมายกำหนด",
      "จัดเตรียมและแปลวุฒิการศึกษาและหนังสือรับรองประสบการณ์",
      "ประสานฝ่ายบุคคลของนายจ้างเพื่อจัดเอกสารนิติบุคคลให้ครบ",
      "ตรวจความสอดคล้องระหว่างสัญญาจ้างกับคำขอที่ยื่น",
      "ติดตามผลและแจ้งกำหนดต่ออายุล่วงหน้า",
      "จัดทำชุดเอกสารสำหรับการตรวจสอบย้อนหลัง",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "กรมการจัดหางาน กระทรวงแรงงาน",
    citationTh: "อ้างอิงพระราชกำหนดการบริหารจัดการการทำงานของคนต่างด้าวและประกาศของกรมการจัดหางาน",
  },
  {
    slug: "marriage",
    th: "จดทะเบียนสมรสและบันทึกฐานะแห่งครอบครัว",
    hubHref: "/marriage",
    summaryTh: "งานเอกสารสำหรับการสมรสในไทยหรือต่างประเทศ และการบันทึกฐานะแห่งครอบครัว",
    documents: [
      { slug: "single-cert", th: "หนังสือรับรองความเป็นโสด" },
      { slug: "affirmation", th: "หนังสือรับรองจากสถานทูตของคู่สมรส" },
      { slug: "id-marriage", th: "หนังสือเดินทางและบัตรประชาชน" },
      { slug: "divorce-doc", th: "ทะเบียนหย่าหรือใบมรณบัตรของคู่สมรสเดิม" },
      { slug: "house-reg", th: "ทะเบียนบ้านและเอกสารที่อยู่" },
      { slug: "prenup", th: "สัญญาก่อนสมรส" },
      { slug: "foreign-marriage", th: "ทะเบียนสมรสที่จดในต่างประเทศ" },
      { slug: "family-record", th: "บันทึกฐานะแห่งครอบครัว" },
    ],
    purposesTh: [
      "ใช้จดทะเบียนสมรสที่สำนักงานเขตหรือที่ว่าการอำเภอ",
      "ใช้จดทะเบียนสมรสในต่างประเทศ",
      "ใช้บันทึกฐานะแห่งครอบครัวหลังสมรสในต่างประเทศ",
      "ใช้ยื่นวีซ่าคู่ครองหลังการสมรส",
      "ใช้ประกอบสิทธิด้านทรัพย์สินและมรดก",
      "ใช้ประกอบการเปลี่ยนคำนำหน้าและนามสกุล",
    ],
    stepsTh: [
      "ตรวจเอกสารของคู่สมรสต่างชาติว่าครบตามที่นายทะเบียนต้องการ",
      "จัดทำคำแปลและรับรองคำแปลของเอกสารจากต่างประเทศ",
      "ประสานล่ามสำหรับวันจดทะเบียนเมื่อคู่สมรสไม่เข้าใจภาษาไทย",
      "ตรวจการสะกดชื่อในทุกฉบับให้ตรงกันก่อนวันจดทะเบียน",
      "ยื่นบันทึกฐานะแห่งครอบครัวเมื่อสมรสในต่างประเทศ",
      "จัดชุดเอกสารสำหรับใช้ต่อในการยื่นวีซ่าและสิทธิอื่น",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "สำนักทะเบียนอำเภอหรือสำนักงานเขต และสถานทูตของคู่สมรส",
    citationTh: "อ้างอิงระเบียบงานทะเบียนครอบครัวของกรมการปกครองและข้อกำหนดของสถานทูตแต่ละประเทศ",
  },
  {
    slug: "pcc",
    th: "หนังสือรับรองความประพฤติ (Police Clearance)",
    hubHref: "/pcc",
    summaryTh: "งานขอหนังสือรับรองความประพฤติของไทยและการนำไปใช้กับหน่วยงานต่างประเทศ",
    documents: [
      { slug: "pcc-form", th: "คำร้องขอหนังสือรับรองความประพฤติ" },
      { slug: "fingerprint", th: "แผ่นพิมพ์ลายนิ้วมือ" },
      { slug: "id-pcc", th: "บัตรประชาชนและหนังสือเดินทาง" },
      { slug: "house-pcc", th: "ทะเบียนบ้าน" },
      { slug: "purpose-doc", th: "เอกสารแสดงวัตถุประสงค์การขอ" },
      { slug: "poa-pcc", th: "หนังสือมอบอำนาจให้ดำเนินการแทน" },
      { slug: "photo", th: "รูปถ่ายตามข้อกำหนด" },
      { slug: "visa-doc-pcc", th: "เอกสารวีซ่าหรือหนังสือตอบรับจากปลายทาง" },
    ],
    purposesTh: [
      "ใช้ยื่นขอวีซ่าถาวรและถิ่นที่อยู่",
      "ใช้ยื่นสมัครงานในต่างประเทศ",
      "ใช้ยื่นขอสัญชาติ",
      "ใช้ประกอบการขึ้นทะเบียนวิชาชีพ",
      "ใช้ประกอบการรับบุตรบุญธรรม",
      "ใช้ยื่นต่อหน่วยงานตรวจสอบประวัติของนายจ้าง",
    ],
    stepsTh: [
      "ตรวจว่าปลายทางต้องการฉบับภาษาใดและต้องรับรองต่ออีกหรือไม่",
      "จัดเตรียมการพิมพ์ลายนิ้วมือให้ถูกรูปแบบสำหรับผู้ที่อยู่ต่างประเทศ",
      "ยื่นคำร้องและติดตามคิวพิจารณา",
      "จัดทำคำแปลและรับรองคำแปลเมื่อได้รับฉบับจริง",
      "ประสานขั้นรับรองต่อเนื่องตามที่ปลายทางกำหนด",
      "แจ้งอายุการใช้งานของหนังสือรับรองเพื่อวางแผนวันยื่น",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "ศูนย์บริการออกหนังสือรับรองความประพฤติ สำนักงานตำรวจแห่งชาติ",
    citationTh: "อ้างอิงประกาศหลักเกณฑ์การขอหนังสือรับรองความประพฤติของสำนักงานตำรวจแห่งชาติ",
  },
  {
    slug: "moj",
    th: "งานรับรองเอกสารกระทรวงยุติธรรมและงานคดี",
    hubHref: "/moj",
    summaryTh: "งานเอกสารที่เกี่ยวข้องกับกระทรวงยุติธรรม กรมคุมประพฤติ และเอกสารคดีที่ต้องใช้ในต่างประเทศ",
    documents: [
      { slug: "court-record", th: "เอกสารคดีและคำพิพากษา" },
      { slug: "probation", th: "หนังสือรับรองจากหน่วยงานคุมประพฤติ" },
      { slug: "legal-aid", th: "เอกสารขอความช่วยเหลือทางกฎหมาย" },
      { slug: "witness", th: "คำให้การและเอกสารพยาน" },
      { slug: "enforce", th: "เอกสารบังคับคดี" },
      { slug: "record-check", th: "การตรวจสอบประวัติทางคดี" },
      { slug: "poa-moj", th: "หนังสือมอบอำนาจในงานคดี" },
      { slug: "translation-moj", th: "คำแปลเอกสารคดีที่ต้องรับรอง" },
    ],
    purposesTh: [
      "ใช้ยื่นต่อศาลต่างประเทศ",
      "ใช้ประกอบการขอวีซ่าและถิ่นที่อยู่",
      "ใช้ประกอบการบังคับคดีข้ามประเทศ",
      "ใช้ประกอบการตรวจสอบประวัติเพื่อทำงาน",
      "ใช้ประกอบการรับรองสถานะทางกฎหมาย",
      "ใช้ประกอบการดำเนินคดีครอบครัวข้ามประเทศ",
    ],
    stepsTh: [
      "ตรวจว่าเอกสารคดีฉบับใดขอคัดรับรองได้และต้องใช้อำนาจใคร",
      "จัดทำคำแปลเอกสารคดีโดยรักษาศัพท์กฎหมายให้ตรงความหมาย",
      "ประสานหน่วยงานเพื่อขอคัดสำเนารับรอง",
      "จัดลำดับการรับรองต่อเนื่องก่อนส่งไปต่างประเทศ",
      "ตรวจความครบถ้วนของเลขคดีและวันที่บนทุกฉบับ",
      "สรุปข้อจำกัดการใช้เอกสารให้ลูกค้าทราบก่อนยื่น",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "หน่วยงานในสังกัดกระทรวงยุติธรรมและศาลที่เกี่ยวข้อง",
    citationTh: "อ้างอิงระเบียบการขอคัดและรับรองสำเนาเอกสารของหน่วยงานในสังกัดกระทรวงยุติธรรม",
  },
  {
    slug: "civilreg",
    th: "งานทะเบียนราษฎรและเอกสารทะเบียน",
    hubHref: "/civil-registration",
    summaryTh: "งานคัด แปล และรับรองเอกสารทะเบียนราษฎรเพื่อใช้ทั้งในไทยและต่างประเทศ",
    documents: [
      { slug: "birth-reg", th: "สูติบัตรและหนังสือรับรองการเกิด" },
      { slug: "death-reg", th: "มรณบัตร" },
      { slug: "house-book", th: "ทะเบียนบ้าน" },
      { slug: "name-change", th: "หนังสือสำคัญการเปลี่ยนชื่อและนามสกุล" },
      { slug: "id-civil", th: "บัตรประจำตัวประชาชน" },
      { slug: "adoption", th: "เอกสารรับบุตรบุญธรรม" },
      { slug: "status", th: "หนังสือรับรองสถานภาพ" },
      { slug: "residence", th: "หนังสือรับรองถิ่นที่อยู่" },
    ],
    purposesTh: [
      "ใช้ยื่นต่อหน่วยงานต่างประเทศ",
      "ใช้ประกอบการขอสัญชาติและถิ่นที่อยู่",
      "ใช้ประกอบสิทธิด้านมรดกและทรัพย์สิน",
      "ใช้ประกอบการสมรสและงานครอบครัว",
      "ใช้ประกอบการเรียนและการทำงาน",
      "ใช้แก้ไขข้อมูลที่คลาดเคลื่อนในเอกสารราชการ",
    ],
    stepsTh: [
      "ตรวจว่าฉบับที่มีอยู่ใช้ได้หรือควรคัดฉบับใหม่ตามอายุที่ปลายทางกำหนด",
      "ประสานสำนักทะเบียนเพื่อขอคัดรับรองสำเนา",
      "จัดทำคำแปลตามรูปแบบมาตรฐานที่หน่วยงานรับรองยอมรับ",
      "ตรวจการสะกดชื่อและวันที่ให้ตรงกันทุกฉบับ",
      "ประสานการรับรองต่อเนื่องเมื่อจะนำไปใช้ต่างประเทศ",
      "จัดชุดเอกสารและสำเนาสำรองให้ลูกค้าเก็บไว้ใช้ครั้งถัดไป",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "สำนักทะเบียนอำเภอหรือสำนักงานเขต กรมการปกครอง",
    citationTh: "อ้างอิงระเบียบสำนักทะเบียนกลางว่าด้วยการจัดทำทะเบียนราษฎร",
  },
  {
    slug: "corporate",
    th: "จดทะเบียนธุรกิจและงานนิติบุคคล",
    hubHref: "/corporate",
    summaryTh: "งานจดทะเบียนบริษัท เปลี่ยนแปลงข้อมูลนิติบุคคล และเอกสารบริษัทสำหรับใช้ต่างประเทศ",
    documents: [
      { slug: "affidavit-co", th: "หนังสือรับรองบริษัท" },
      { slug: "moa", th: "หนังสือบริคณห์สนธิและข้อบังคับ" },
      { slug: "shareholder", th: "บัญชีรายชื่อผู้ถือหุ้น" },
      { slug: "minutes", th: "รายงานการประชุมและมติกรรมการ" },
      { slug: "branch", th: "เอกสารจัดตั้งสาขาและสำนักงานผู้แทน" },
      { slug: "license-co", th: "ใบทะเบียนพาณิชย์และใบอนุญาตที่เกี่ยวข้อง" },
      { slug: "financial-co", th: "งบการเงินและเอกสารบัญชี" },
      { slug: "poa-co", th: "หนังสือมอบอำนาจกรรมการ" },
    ],
    purposesTh: [
      "ใช้จดทะเบียนจัดตั้งบริษัทในไทย",
      "ใช้เปลี่ยนแปลงกรรมการและทุนจดทะเบียน",
      "ใช้ประกอบการเปิดบัญชีธนาคารนิติบุคคล",
      "ใช้ยื่นต่อคู่ค้าและหน่วยงานในต่างประเทศ",
      "ใช้ประกอบการขอใบอนุญาตประกอบธุรกิจ",
      "ใช้ประกอบการลงทุนและร่วมทุน",
    ],
    stepsTh: [
      "ตรวจโครงสร้างผู้ถือหุ้นและเงื่อนไขที่เกี่ยวกับผู้ถือหุ้นต่างชาติ",
      "จัดเตรียมชุดเอกสารจดทะเบียนและตรวจถ้อยคำวัตถุประสงค์",
      "ประสานการลงนามของกรรมการที่อยู่ต่างประเทศพร้อมรับรองลายมือชื่อ",
      "แปลและรับรองเอกสารบริษัทเพื่อใช้กับคู่ค้าในต่างประเทศ",
      "ยื่นจดทะเบียนและติดตามผลจนได้รับหนังสือรับรองฉบับใหม่",
      "จัดทำปฏิทินหน้าที่ตามกฎหมายรายปีให้บริษัท",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "กรมพัฒนาธุรกิจการค้า กระทรวงพาณิชย์",
    citationTh: "อ้างอิงคู่มือการจดทะเบียนนิติบุคคลของกรมพัฒนาธุรกิจการค้า",
  },
  {
    slug: "accounting",
    th: "บัญชี ภาษี และเงินเดือน",
    hubHref: "/accounting",
    summaryTh: "งานจัดทำบัญชี ยื่นภาษี และเอกสารการเงินสำหรับนิติบุคคลและชาวต่างชาติในไทย",
    documents: [
      { slug: "monthly", th: "เอกสารบัญชีรายเดือน" },
      { slug: "vat", th: "แบบยื่นภาษีมูลค่าเพิ่ม" },
      { slug: "wht", th: "หนังสือรับรองการหักภาษี ณ ที่จ่าย" },
      { slug: "payroll", th: "เอกสารเงินเดือนและประกันสังคม" },
      { slug: "pnd", th: "แบบแสดงรายการภาษีเงินได้" },
      { slug: "audit", th: "งบการเงินและรายงานผู้สอบบัญชี" },
      { slug: "tax-resident", th: "หนังสือรับรองการมีถิ่นที่อยู่ทางภาษี" },
      { slug: "invoice", th: "ใบกำกับภาษีและเอกสารประกอบรายได้" },
    ],
    purposesTh: [
      "ใช้ยื่นภาษีตามกำหนดรายเดือนและรายปี",
      "ใช้ประกอบการขอคืนภาษี",
      "ใช้ประกอบการขอสินเชื่อและเปิดบัญชี",
      "ใช้ยื่นต่อหน่วยงานต่างประเทศเพื่อพิสูจน์รายได้",
      "ใช้ประกอบการขอใบอนุญาตทำงานและวีซ่า",
      "ใช้ประกอบการตรวจสอบของหน่วยงานภาษี",
    ],
    stepsTh: [
      "ตรวจความครบถ้วนของเอกสารรายได้และรายจ่ายก่อนบันทึกบัญชี",
      "จัดทำและยื่นแบบภาษีตามกำหนดเวลา",
      "จัดทำเอกสารเงินเดือนและนำส่งประกันสังคม",
      "แปลและรับรองเอกสารการเงินเมื่อจะใช้ในต่างประเทศ",
      "ประสานผู้สอบบัญชีเพื่อปิดงบประจำปี",
      "จัดทำปฏิทินหน้าที่ทางภาษีให้ลูกค้าติดตามได้",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "กรมสรรพากรและสำนักงานประกันสังคม",
    citationTh: "อ้างอิงประมวลรัษฎากรและคำแนะนำการยื่นแบบของกรมสรรพากร",
  },
  {
    slug: "license",
    th: "ใบอนุญาตประกอบธุรกิจเฉพาะทาง",
    hubHref: "/license",
    summaryTh: "งานขอใบอนุญาตเฉพาะทางสำหรับธุรกิจที่กฎหมายกำหนดให้ต้องได้รับอนุญาตก่อนดำเนินการ",
    documents: [
      { slug: "app-form", th: "คำขอใบอนุญาตและเอกสารประกอบ" },
      { slug: "premise", th: "เอกสารสถานประกอบการและผังพื้นที่" },
      { slug: "staff", th: "เอกสารคุณสมบัติบุคลากร" },
      { slug: "product-doc", th: "เอกสารผลิตภัณฑ์และมาตรฐาน" },
      { slug: "safety", th: "เอกสารความปลอดภัยและสุขอนามัย" },
      { slug: "corporate-license", th: "เอกสารนิติบุคคลของผู้ขออนุญาต" },
      { slug: "renew-license", th: "เอกสารต่ออายุใบอนุญาต" },
      { slug: "poa-license", th: "หนังสือมอบอำนาจยื่นคำขอ" },
    ],
    purposesTh: [
      "ใช้ขอใบอนุญาตก่อนเริ่มประกอบกิจการ",
      "ใช้ต่ออายุใบอนุญาตประจำปี",
      "ใช้ประกอบการขยายสาขาและเพิ่มประเภทกิจการ",
      "ใช้ประกอบการตรวจสอบของหน่วยงานกำกับ",
      "ใช้ประกอบการขอสิทธิประโยชน์การลงทุน",
      "ใช้ประกอบการแสดงต่อคู่ค้าและลูกค้าองค์กร",
    ],
    stepsTh: [
      "ตรวจว่าธุรกิจเข้าข่ายใบอนุญาตประเภทใดและหน่วยงานใดเป็นผู้พิจารณา",
      "จัดเตรียมเอกสารสถานประกอบการและคุณสมบัติบุคลากร",
      "จัดทำคำแปลเอกสารของผู้ถือหุ้นหรือบุคลากรต่างชาติ",
      "ยื่นคำขอและประสานการตรวจสถานที่",
      "ติดตามผลและแก้ไขเอกสารตามข้อสังเกตของเจ้าหน้าที่",
      "จัดทำปฏิทินการต่ออายุและเงื่อนไขที่ต้องปฏิบัติต่อเนื่อง",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "หน่วยงานผู้ออกใบอนุญาตตามประเภทกิจการ",
    citationTh: "อ้างอิงประกาศหลักเกณฑ์การขออนุญาตของหน่วยงานผู้กำกับดูแลแต่ละประเภทกิจการ",
  },
  {
    slug: "due-diligence",
    th: "ตรวจสอบสถานะและประวัติองค์กร (Due Diligence)",
    hubHref: "/due-diligence",
    summaryTh: "งานตรวจสอบข้อมูลนิติบุคคล เอกสารสิทธิ และความเสี่ยงก่อนทำธุรกรรม",
    documents: [
      { slug: "company-search", th: "ข้อมูลจดทะเบียนนิติบุคคล" },
      { slug: "financial-dd", th: "งบการเงินย้อนหลัง" },
      { slug: "litigation-dd", th: "ข้อมูลคดีความที่เกี่ยวข้อง" },
      { slug: "asset", th: "เอกสารสิทธิในทรัพย์สิน" },
      { slug: "contract-dd", th: "สัญญาสำคัญของกิจการ" },
      { slug: "license-dd", th: "ใบอนุญาตที่กิจการถืออยู่" },
      { slug: "shareholding", th: "โครงสร้างผู้ถือหุ้นและผู้มีอำนาจ" },
      { slug: "compliance-dd", th: "เอกสารการปฏิบัติตามกฎหมาย" },
    ],
    purposesTh: [
      "ใช้ประกอบการซื้อขายกิจการ",
      "ใช้ประกอบการร่วมทุนและลงทุน",
      "ใช้ประกอบการคัดเลือกคู่ค้า",
      "ใช้ประกอบการขอสินเชื่อ",
      "ใช้ประกอบการประเมินความเสี่ยงก่อนทำสัญญา",
      "ใช้ประกอบการตรวจสอบภายในขององค์กร",
    ],
    stepsTh: [
      "กำหนดขอบเขตการตรวจสอบร่วมกับลูกค้าก่อนเริ่มงาน",
      "คัดเอกสารจากหน่วยงานทะเบียนและตรวจความครบถ้วน",
      "แปลเอกสารสำคัญเพื่อให้ผู้ตัดสินใจต่างชาติอ่านได้",
      "สรุปประเด็นความเสี่ยงเป็นรายข้อพร้อมเอกสารอ้างอิง",
      "จัดทำรายงานสรุปที่แยกข้อเท็จจริงออกจากข้อสังเกต",
      "ประชุมสรุปผลและเสนอแนวทางจัดการความเสี่ยง",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "แหล่งข้อมูลทะเบียนราชการและเอกสารที่คู่สัญญาจัดส่ง",
    citationTh: "อ้างอิงข้อมูลทะเบียนนิติบุคคลของกรมพัฒนาธุรกิจการค้าและเอกสารที่ได้รับจากคู่สัญญา",
  },
  {
    slug: "estate",
    th: "พินัยกรรมและการจัดการมรดก",
    hubHref: "/estate",
    summaryTh: "งานจัดทำพินัยกรรม การตั้งผู้จัดการมรดก และเอกสารมรดกที่มีทรัพย์สินข้ามประเทศ",
    documents: [
      { slug: "will", th: "พินัยกรรม" },
      { slug: "death-cert", th: "มรณบัตร" },
      { slug: "heir", th: "เอกสารแสดงความเป็นทายาท" },
      { slug: "asset-list", th: "บัญชีทรัพย์สินและหนี้สิน" },
      { slug: "court-estate", th: "คำร้องและคำสั่งศาลตั้งผู้จัดการมรดก" },
      { slug: "bank-estate", th: "เอกสารบัญชีธนาคารของผู้ตาย" },
      { slug: "land", th: "โฉนดที่ดินและเอกสารสิทธิ" },
      { slug: "foreign-estate", th: "เอกสารมรดกจากต่างประเทศ" },
    ],
    purposesTh: [
      "ใช้จัดทำพินัยกรรมให้มีผลตามกฎหมาย",
      "ใช้ยื่นคำร้องตั้งผู้จัดการมรดก",
      "ใช้ประกอบการโอนทรัพย์สินให้ทายาท",
      "ใช้ประกอบการจัดการทรัพย์สินในต่างประเทศ",
      "ใช้ประกอบการปิดบัญชีและเรียกร้องสิทธิประกัน",
      "ใช้ประกอบการแบ่งทรัพย์สินระหว่างทายาท",
    ],
    stepsTh: [
      "ตรวจสถานะทรัพย์สินและทายาทก่อนร่างเอกสาร",
      "ร่างพินัยกรรมให้สอดคล้องกับกฎหมายไทยและทรัพย์สินในต่างประเทศ",
      "จัดพยานและขั้นตอนลงนามให้ถูกต้องตามแบบที่กฎหมายกำหนด",
      "แปลและรับรองเอกสารมรดกที่ออกจากต่างประเทศ",
      "ยื่นคำร้องต่อศาลและติดตามกระบวนพิจารณา",
      "ประสานการโอนทรัพย์สินกับหน่วยงานที่เกี่ยวข้อง",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "ศาลที่มีเขตอำนาจและหน่วยงานทะเบียนทรัพย์สิน",
    citationTh: "อ้างอิงประมวลกฎหมายแพ่งและพาณิชย์ บรรพ 6 ว่าด้วยมรดก",
  },
  {
    slug: "litigation",
    th: "ว่าความและงานคดี",
    hubHref: "/litigation",
    summaryTh: "งานดำเนินคดีแพ่ง อาญา ครอบครัว และแรงงาน รวมถึงคดีที่มีคู่ความต่างชาติ",
    documents: [
      { slug: "complaint", th: "คำฟ้องและคำให้การ" },
      { slug: "evidence", th: "พยานเอกสารและพยานวัตถุ" },
      { slug: "poa-lit", th: "ใบแต่งทนายความ" },
      { slug: "contract-lit", th: "สัญญาที่เป็นมูลคดี" },
      { slug: "judgment", th: "คำพิพากษาและคำสั่ง" },
      { slug: "execution", th: "เอกสารบังคับคดี" },
      { slug: "settlement", th: "สัญญาประนีประนอมยอมความ" },
      { slug: "foreign-doc-lit", th: "เอกสารจากต่างประเทศที่ใช้ในคดี" },
    ],
    purposesTh: [
      "ใช้ฟ้องและต่อสู้คดีในศาลไทย",
      "ใช้ประกอบการไกล่เกลี่ยและเจรจา",
      "ใช้ประกอบการบังคับคดีตามคำพิพากษา",
      "ใช้ประกอบคดีครอบครัวและอำนาจปกครองบุตร",
      "ใช้ประกอบคดีแรงงานและข้อพิพาทการจ้าง",
      "ใช้ประกอบคดีที่มีคู่ความอยู่ต่างประเทศ",
    ],
    stepsTh: [
      "ประเมินข้อเท็จจริงและอายุความก่อนรับดำเนินคดี",
      "จัดทำคำคู่ความและรวบรวมพยานหลักฐาน",
      "แปลและรับรองเอกสารต่างประเทศเพื่อใช้เป็นพยานเอกสาร",
      "จัดล่ามในชั้นศาลและชั้นสอบสวนเมื่อคู่ความไม่เข้าใจภาษาไทย",
      "รายงานความคืบหน้าของคดีเป็นระยะและเตรียมคู่ความก่อนวันนัด",
      "ดำเนินการชั้นบังคับคดีหรือประนีประนอมตามผลของคดี",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "ศาลที่มีเขตอำนาจและหน่วยงานในกระบวนการยุติธรรม",
    citationTh: "อ้างอิงประมวลกฎหมายวิธีพิจารณาความแพ่งและประมวลกฎหมายวิธีพิจารณาความอาญา",
  },
  {
    slug: "ip",
    th: "ทรัพย์สินทางปัญญา",
    hubHref: "/ip",
    summaryTh: "งานจดทะเบียนเครื่องหมายการค้า ลิขสิทธิ์ สิทธิบัตร และการคุ้มครองสิทธิ",
    documents: [
      { slug: "tm", th: "คำขอจดทะเบียนเครื่องหมายการค้า" },
      { slug: "copyright", th: "เอกสารแจ้งข้อมูลลิขสิทธิ์" },
      { slug: "patent", th: "คำขอสิทธิบัตรและอนุสิทธิบัตร" },
      { slug: "design", th: "คำขอสิทธิบัตรการออกแบบผลิตภัณฑ์" },
      { slug: "assignment", th: "สัญญาโอนสิทธิและอนุญาตให้ใช้สิทธิ" },
      { slug: "priority", th: "เอกสารอ้างสิทธิจากคำขอในต่างประเทศ" },
      { slug: "opposition", th: "คำคัดค้านและคำโต้แย้ง" },
      { slug: "poa-ip", th: "หนังสือมอบอำนาจตัวแทน" },
    ],
    purposesTh: [
      "ใช้จดทะเบียนสิทธิในประเทศไทย",
      "ใช้ขยายความคุ้มครองไปต่างประเทศ",
      "ใช้ประกอบการอนุญาตให้ใช้สิทธิ",
      "ใช้ประกอบการดำเนินคดีละเมิดสิทธิ",
      "ใช้ประกอบการระดมทุนและประเมินมูลค่ากิจการ",
      "ใช้ประกอบการขายและโอนสิทธิ",
    ],
    stepsTh: [
      "สืบค้นความเหมือนคล้ายก่อนยื่นคำขอ",
      "จัดทำรายการสินค้าและบริการให้ตรงกับการใช้จริง",
      "แปลและรับรองเอกสารของผู้ขอที่อยู่ต่างประเทศ",
      "ยื่นคำขอและติดตามคำสั่งของนายทะเบียน",
      "จัดทำคำชี้แจงเมื่อได้รับข้อโต้แย้ง",
      "จัดทำปฏิทินการต่ออายุและการใช้สิทธิต่อเนื่อง",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "กรมทรัพย์สินทางปัญญา กระทรวงพาณิชย์",
    citationTh: "อ้างอิงคู่มือการยื่นคำขอของกรมทรัพย์สินทางปัญญา",
  },
  {
    slug: "pdpa",
    th: "งานคุ้มครองข้อมูลส่วนบุคคล (PDPA)",
    hubHref: "/pdpa",
    summaryTh: "งานจัดทำเอกสารและกระบวนการให้สอดคล้องกับกฎหมายคุ้มครองข้อมูลส่วนบุคคล",
    documents: [
      { slug: "policy", th: "นโยบายคุ้มครองข้อมูลส่วนบุคคล" },
      { slug: "consent-pdpa", th: "แบบขอความยินยอม" },
      { slug: "ropa", th: "บันทึกรายการกิจกรรมการประมวลผล" },
      { slug: "dpa", th: "ข้อตกลงประมวลผลข้อมูลกับคู่ค้า" },
      { slug: "dsr", th: "กระบวนการรับคำขอใช้สิทธิของเจ้าของข้อมูล" },
      { slug: "breach", th: "แผนรับมือเหตุละเมิดข้อมูล" },
      { slug: "training", th: "เอกสารอบรมพนักงาน" },
      { slug: "audit-pdpa", th: "รายงานการตรวจสอบความสอดคล้อง" },
    ],
    purposesTh: [
      "ใช้ปรับกระบวนการภายในให้สอดคล้องกฎหมาย",
      "ใช้ประกอบสัญญากับคู่ค้าและผู้ให้บริการ",
      "ใช้ตอบคำขอใช้สิทธิของเจ้าของข้อมูล",
      "ใช้ประกอบการตรวจสอบขององค์กรลูกค้า",
      "ใช้ประกอบการขยายบริการไปต่างประเทศ",
      "ใช้ประกอบการรับมือเหตุละเมิดข้อมูล",
    ],
    stepsTh: [
      "สำรวจข้อมูลส่วนบุคคลที่องค์กรเก็บและฐานการประมวลผล",
      "จัดทำเอกสารนโยบายและแบบยินยอมให้ใช้งานได้จริง",
      "จัดทำบันทึกรายการกิจกรรมการประมวลผล",
      "ทบทวนสัญญากับผู้ประมวลผลข้อมูล",
      "ออกแบบขั้นตอนรับคำขอใช้สิทธิและเหตุละเมิด",
      "อบรมทีมงานและกำหนดรอบทบทวนประจำปี",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "สำนักงานคณะกรรมการคุ้มครองข้อมูลส่วนบุคคล",
    citationTh: "อ้างอิงพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 และประกาศที่เกี่ยวข้อง",
  },
  {
    slug: "interpreter",
    th: "บริการล่าม",
    hubHref: "/interpreter",
    summaryTh: "งานล่ามในชั้นศาล ชั้นสอบสวน งานทะเบียน งานธุรกิจ และงานทางการแพทย์",
    documents: [
      { slug: "court-int", th: "งานล่ามในชั้นศาลและชั้นสอบสวน" },
      { slug: "registry-int", th: "งานล่ามที่สำนักทะเบียนและอำเภอ" },
      { slug: "business-int", th: "งานล่ามการประชุมและเจรจาธุรกิจ" },
      { slug: "medical-int", th: "งานล่ามทางการแพทย์" },
      { slug: "signing", th: "งานล่ามในวันลงนามเอกสาร" },
      { slug: "remote", th: "งานล่ามทางโทรศัพท์และวิดีโอ" },
      { slug: "site", th: "งานล่ามภาคสนามและตรวจโรงงาน" },
      { slug: "training-int", th: "งานล่ามในการอบรมและสัมมนา" },
    ],
    purposesTh: [
      "ใช้ในกระบวนการทางกฎหมาย",
      "ใช้ในการจดทะเบียนและติดต่อราชการ",
      "ใช้ในการเจรจาธุรกิจและลงนามสัญญา",
      "ใช้ในการรักษาพยาบาลและการสื่อสารกับแพทย์",
      "ใช้ในการอบรมและถ่ายทอดความรู้",
      "ใช้ในการประชุมออนไลน์ข้ามประเทศ",
    ],
    stepsTh: [
      "สอบถามบริบทงานและศัพท์เฉพาะเพื่อจับคู่ล่ามให้ตรงสาขา",
      "ส่งเอกสารประกอบให้ล่ามเตรียมคำศัพท์ล่วงหน้า",
      "ยืนยันสถานที่ เวลา และรูปแบบล่าม (พูดตาม/พูดพร้อม/ทางไกล)",
      "ประสานงานกับเจ้าหน้าที่หน้างานเรื่องขั้นตอนและลำดับการพูด",
      "สรุปประเด็นสำคัญหลังงานให้ลูกค้าใช้อ้างอิง",
      "จัดล่ามสำรองสำหรับงานที่มีกำหนดเวลาแน่นอน",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "หน่วยงานเจ้าของกระบวนการที่ล่ามเข้าร่วม",
    citationTh: "อ้างอิงระเบียบว่าด้วยการใช้ล่ามในกระบวนพิจารณาและข้อกำหนดของหน่วยงานที่เกี่ยวข้อง",
  },
  {
    slug: "council",
    th: "หนังสือรับรองสภาวิชาชีพและใบอนุญาตวิชาชีพ",
    hubHref: "/council",
    summaryTh: "งานขอหนังสือรับรองจากสภาวิชาชีพและการนำใบอนุญาตวิชาชีพไปใช้ต่างประเทศ",
    documents: [
      { slug: "license-prof", th: "ใบอนุญาตประกอบวิชาชีพ" },
      { slug: "good-standing", th: "หนังสือรับรองสถานะสมาชิก" },
      { slug: "curriculum", th: "รายละเอียดหลักสูตรและหน่วยกิต" },
      { slug: "experience", th: "หนังสือรับรองประสบการณ์ทำงาน" },
      { slug: "cpd", th: "หลักฐานการศึกษาต่อเนื่อง" },
      { slug: "degree-prof", th: "ปริญญาบัตรและทรานสคริปต์" },
      { slug: "id-prof", th: "เอกสารแสดงตนของผู้ประกอบวิชาชีพ" },
      { slug: "poa-prof", th: "หนังสือมอบอำนาจยื่นแทน" },
    ],
    purposesTh: [
      "ใช้ขึ้นทะเบียนวิชาชีพในต่างประเทศ",
      "ใช้ยื่นประเมินวุฒิและเทียบคุณสมบัติ",
      "ใช้สมัครงานในสายวิชาชีพ",
      "ใช้ยื่นขอวีซ่าทำงานประเภทผู้เชี่ยวชาญ",
      "ใช้ต่ออายุสถานะสมาชิกสภาวิชาชีพ",
      "ใช้ประกอบการอบรมและสอบใบอนุญาต",
    ],
    stepsTh: [
      "ตรวจว่าองค์กรปลายทางต้องการหนังสือรับรองรูปแบบใด",
      "ยื่นคำขอต่อสภาวิชาชีพและติดตามคิว",
      "แปลและรับรองคำแปลใบอนุญาตและหลักสูตร",
      "ประสานการรับรองต่อเนื่องเพื่อใช้ในต่างประเทศ",
      "ตรวจความสอดคล้องของชื่อและเลขที่ใบอนุญาตทุกฉบับ",
      "จัดชุดเอกสารพร้อมสารบัญให้ผู้ประเมินตรวจได้เร็ว",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "สภาวิชาชีพที่กำกับดูแลแต่ละสาขา",
    citationTh: "อ้างอิงระเบียบการออกหนังสือรับรองของสภาวิชาชีพแต่ละแห่ง",
  },
  {
    slug: "embassy",
    th: "งานเอกสารกับสถานทูตและสถานกงสุล",
    hubHref: "/embassy",
    summaryTh: "งานประสานเอกสารกับสถานทูตต่างประเทศในไทยและสถานทูตไทยในต่างประเทศ",
    documents: [
      { slug: "affirmation-emb", th: "หนังสือรับรองสถานภาพจากสถานทูต" },
      { slug: "passport-emb", th: "งานหนังสือเดินทางและเอกสารเดินทาง" },
      { slug: "notarial-emb", th: "งานนิติกรณ์ที่สถานทูต" },
      { slug: "visa-emb", th: "เอกสารประกอบคำร้องขอวีซ่า" },
      { slug: "power-emb", th: "หนังสือมอบอำนาจที่ทำต่อหน้ากงสุล" },
      { slug: "civil-emb", th: "การแจ้งเกิด แจ้งตาย และงานทะเบียนที่สถานทูต" },
      { slug: "translation-emb", th: "คำแปลที่สถานทูตกำหนดรูปแบบ" },
      { slug: "appointment", th: "การนัดหมายและแบบฟอร์มออนไลน์" },
    ],
    purposesTh: [
      "ใช้ประกอบการสมรสกับชาวต่างชาติ",
      "ใช้ประกอบการยื่นวีซ่า",
      "ใช้ประกอบงานทะเบียนของคนไทยในต่างประเทศ",
      "ใช้ประกอบการมอบอำนาจข้ามประเทศ",
      "ใช้ประกอบการรับรองเอกสารก่อนนำไปใช้ปลายทาง",
      "ใช้ประกอบการต่ออายุเอกสารเดินทาง",
    ],
    stepsTh: [
      "ตรวจข้อกำหนดและรอบนัดหมายของสถานทูตแต่ละแห่ง",
      "เตรียมแบบฟอร์มและเอกสารตามรายการที่ประกาศไว้",
      "จัดทำคำแปลในรูปแบบที่สถานทูตนั้นยอมรับ",
      "จองคิวและเตรียมผู้ยื่นให้พร้อมสำหรับการสัมภาษณ์หรือลงนาม",
      "ติดตามผลและรับเอกสารคืนตามรอบที่กำหนด",
      "ตรวจเอกสารที่ได้รับก่อนนำไปใช้ขั้นถัดไป",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "สถานเอกอัครราชทูตและสถานกงสุลที่เกี่ยวข้อง",
    citationTh: "อ้างอิงประกาศบริการและรายการเอกสารที่เผยแพร่โดยสถานทูตหรือสถานกงสุลแต่ละแห่ง",
  },
  {
    slug: "pension",
    th: "บำนาญต่างประเทศและหนังสือรับรองการมีชีวิต",
    hubHref: "/pension",
    summaryTh: "งานเอกสารสำหรับผู้รับบำนาญจากต่างประเทศที่พำนักในไทย",
    documents: [
      { slug: "life-cert", th: "หนังสือรับรองการมีชีวิต (Life Certificate)" },
      { slug: "pension-form", th: "แบบฟอร์มของกองทุนบำนาญ" },
      { slug: "address-proof", th: "หลักฐานที่อยู่ปัจจุบัน" },
      { slug: "bank-pension", th: "เอกสารบัญชีรับเงินบำนาญ" },
      { slug: "id-pension", th: "หนังสือเดินทางและเอกสารแสดงตน" },
      { slug: "marital", th: "เอกสารสถานภาพสมรสของผู้รับบำนาญ" },
      { slug: "tax-pension", th: "เอกสารภาษีและถิ่นที่อยู่ทางภาษี" },
      { slug: "survivor", th: "เอกสารสิทธิของผู้รับผลประโยชน์" },
    ],
    purposesTh: [
      "ใช้ยืนยันการมีชีวิตประจำปีต่อกองทุนบำนาญ",
      "ใช้ประกอบการเปลี่ยนบัญชีรับเงิน",
      "ใช้ประกอบการแจ้งเปลี่ยนที่อยู่",
      "ใช้ประกอบการขอสิทธิผู้รับผลประโยชน์",
      "ใช้ประกอบการยื่นภาษีในประเทศต้นทาง",
      "ใช้ประกอบการต่ออายุสิทธิประโยชน์",
    ],
    stepsTh: [
      "ตรวจแบบฟอร์มของกองทุนว่าต้องลงนามต่อหน้าผู้ใด",
      "จัดการรับรองลายมือชื่อในรูปแบบที่กองทุนยอมรับ",
      "แปลและรับรองเอกสารประกอบเมื่อกองทุนกำหนด",
      "ประสานขั้นรับรองต่อเนื่องเมื่อปลายทางต้องการ",
      "จัดส่งเอกสารไปต่างประเทศพร้อมหมายเลขติดตาม",
      "บันทึกรอบครบกำหนดปีถัดไปให้ลูกค้าทราบล่วงหน้า",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "กองทุนบำนาญหรือหน่วยงานประกันสังคมของประเทศต้นทาง",
    citationTh: "อ้างอิงแบบฟอร์มและคำแนะนำที่เผยแพร่โดยกองทุนบำนาญของประเทศต้นทาง",
  },
  {
    slug: "misc-official",
    th: "งานเอกสารราชการอื่น ๆ",
    hubHref: "/misc-official",
    summaryTh: "งานเอกสารราชการที่ไม่ได้อยู่ในหมวดหลัก เช่น ใบขับขี่ ที่ดิน และหนังสือรับรองเฉพาะเรื่อง",
    documents: [
      { slug: "driving", th: "ใบขับขี่และใบขับขี่สากล" },
      { slug: "land-doc", th: "เอกสารสิทธิที่ดินและสิ่งปลูกสร้าง" },
      { slug: "vehicle", th: "ทะเบียนรถและเอกสารกรรมสิทธิ์" },
      { slug: "education-misc", th: "หนังสือรับรองจากสถานศึกษา" },
      { slug: "military", th: "เอกสารทางทหารและการเกณฑ์ทหาร" },
      { slug: "insurance-misc", th: "เอกสารประกันและสิทธิรักษาพยาบาล" },
      { slug: "utility", th: "หลักฐานที่อยู่และสาธารณูปโภค" },
      { slug: "cert-misc", th: "หนังสือรับรองเฉพาะเรื่องจากหน่วยงาน" },
    ],
    purposesTh: [
      "ใช้ยื่นต่อหน่วยงานราชการในไทย",
      "ใช้ยื่นต่อหน่วยงานในต่างประเทศ",
      "ใช้ประกอบธุรกรรมทรัพย์สิน",
      "ใช้ประกอบการขอสิทธิและสวัสดิการ",
      "ใช้ประกอบการเดินทางและการพำนัก",
      "ใช้ประกอบการยืนยันตัวตนและที่อยู่",
    ],
    stepsTh: [
      "ตรวจว่าหน่วยงานใดเป็นผู้ออกเอกสารและต้องใช้หลักฐานใดประกอบ",
      "ยื่นคำขอหรือประสานหน่วยงานแทนลูกค้าตามที่มอบอำนาจ",
      "จัดทำคำแปลและรับรองคำแปลตามการใช้งานจริง",
      "ตรวจอายุเอกสารและรูปแบบที่ปลายทางยอมรับ",
      "จัดส่งเอกสารและติดตามสถานะจนถึงมือผู้รับ",
      "สรุปขั้นตอนที่ต้องทำซ้ำในรอบถัดไป",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "หน่วยงานราชการเจ้าของเรื่องแต่ละประเภทเอกสาร",
    citationTh: "อ้างอิงประกาศและคู่มือประชาชนของหน่วยงานเจ้าของเรื่องแต่ละแห่ง",
  },
  {
    slug: "abroad",
    th: "บริการสำหรับคนไทยในต่างประเทศ",
    hubHref: "/abroad",
    summaryTh: "งานเอกสารไทยสำหรับคนไทยที่พำนักอยู่ต่างประเทศและต้องดำเนินการในไทย",
    documents: [
      { slug: "poa-abroad", th: "หนังสือมอบอำนาจจากต่างประเทศ" },
      { slug: "civil-abroad", th: "เอกสารทะเบียนราษฎรของไทย" },
      { slug: "property-abroad", th: "เอกสารทรัพย์สินในประเทศไทย" },
      { slug: "bank-abroad", th: "เอกสารธนาคารและการเงินในไทย" },
      { slug: "court-abroad", th: "เอกสารคดีที่ต้องดำเนินการในไทย" },
      { slug: "family-abroad", th: "เอกสารครอบครัวและทะเบียนสมรส" },
      { slug: "education-abroad", th: "เอกสารการศึกษาจากสถาบันไทย" },
      { slug: "tax-abroad", th: "เอกสารภาษีและรายได้ในไทย" },
    ],
    purposesTh: [
      "ใช้มอบอำนาจให้ดำเนินการแทนในไทย",
      "ใช้ประกอบธุรกรรมทรัพย์สินในไทย",
      "ใช้ประกอบงานทะเบียนราษฎรของไทย",
      "ใช้ประกอบคดีที่อยู่ในศาลไทย",
      "ใช้ประกอบการยื่นเอกสารต่อหน่วยงานปลายทาง",
      "ใช้ประกอบการจัดการมรดกในไทย",
    ],
    stepsTh: [
      "ยืนยันว่าลูกค้าต้องลงนามต่อหน้าผู้ใดในประเทศที่พำนัก",
      "ร่างเอกสารสองภาษาเพื่อให้ลูกค้าตรวจก่อนลงนาม",
      "ประสานการรับรองที่สถานทูตไทยหรือหน่วยงานปลายทาง",
      "รับเอกสารกลับไทยและดำเนินการต่อกับหน่วยงานที่เกี่ยวข้อง",
      "รายงานความคืบหน้าตามรอบเวลาที่ลูกค้าสะดวก",
      "จัดส่งเอกสารผลลัพธ์กลับไปยังที่อยู่ในต่างประเทศ",
    ],
    extrasTh: COMMON_EXTRAS,
    authorityTh: "สถานเอกอัครราชทูตไทยหรือสถานกงสุลใหญ่ และหน่วยงานราชการไทยเจ้าของเรื่อง",
    citationTh: "อ้างอิงประกาศบริการนิติกรณ์ของสถานเอกอัครราชทูตไทยและระเบียบของหน่วยงานไทยที่เกี่ยวข้อง",
  },
];

export const SERVICE_CASES_BY_SLUG: Record<string, ServiceCaseConfig> = Object.fromEntries(
  SERVICE_CASES.map((s) => [s.slug, s]),
);

const topicCache = new Map<string, CaseTopic[]>();

/** Topics are generated lazily and cached so SSR builds them once. */
export function serviceCaseTopics(slug: string): CaseTopic[] {
  const cached = topicCache.get(slug);
  if (cached) return cached;
  const cfg = SERVICE_CASES_BY_SLUG[slug];
  if (!cfg) return [];
  const topics = buildServiceTopics(cfg);
  topicCache.set(slug, topics);
  return topics;
}

export function serviceCaseCount(slug: string): number {
  return serviceCaseTopics(slug).reduce((n, t) => n + t.cases.length, 0);
}
